import {
  EditableListWithPlaceholder,
  type EditableListProps,
} from '@databases/editable-list';
import EntryPicker from '@databases/field-picker/EntryPicker';
import { messages } from '@databases/intl/messages';
import { logError } from '@databases/logs';
import { useCallbackEffect } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { AsyncOperationStatus } from '@databases/redux-types';
import { currentSnackbarState } from '@databases/snackbar/snackbar';
import {
  type DatabaseEntry,
  DatabaseFieldType,
  type RawDatabaseValue,
} from '@databases/types/database';
import { MultiValueChangeType } from '@databases/utils/database/changesets';
import { isNonEmptyEntryLinkValue } from '@databases/utils/database/field-types/entryLink';
import { getDatabaseEntryOptional, getEntryName } from '@databases/utils/database/utils';
import { loadDatabaseByContentId } from '@databases/utils/redux/databases/actions';
import {
  selectDatabaseByContentIdOptional,
  selectDatabaseByContentIdStatus,
} from '@databases/utils/redux/databases/selectors';
import { generateId } from '@databases/utils/utils';
import React, { type FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { type EditComponentProps } from './types';

type Value = {
  entryRef: NonNullable<RawDatabaseValue<DatabaseFieldType.ENTRY_LINK>>[0];
  entry?: DatabaseEntry;
};

const getId = (value: Value): string => value.entryRef._id;

const getNewValue = (): Value => ({
  entryRef: { _id: generateId() },
});

const EntryLinkValueEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.ENTRY_LINK>
> = ({ onChange, onFocus, onBlur, autoFocus, value, field }) => {
  const {
    databaseSpace,
    databaseId,
    multi,
    contentId: databaseContentId,
  } = field.settings;
  const intl = useIntl();
  const { formatMessage } = intl;
  const [isPending, targetDatabaseDraft] = defaultStoreUtils.useShallowEqualSelector(
    (state) => {
      if (databaseContentId) {
        const targetDatabaseStatus = selectDatabaseByContentIdStatus(state, {
          contentId: databaseContentId,
        });
        const targetDatabase = selectDatabaseByContentIdOptional(state, {
          contentId: databaseContentId,
        });
        return [
          targetDatabaseStatus?.status === AsyncOperationStatus.PENDING,
          targetDatabase &&
            selectDatabaseDraftByContentId(state, {
              contentId: databaseContentId,
              includeDeleted: false,
            }),
        ] as const;
      } else {
        return [false, undefined];
      }
    }
  );

  const dispatch = defaultStoreUtils.useDispatch();
  useCallbackEffect(
    useCallback(async () => {
      try {
        if (databaseContentId) {
          await dispatch(loadDatabaseByContentId(databaseContentId));
        }
        // eslint-disable-next-line canvas/no-swallow-errors-in-catch
      } catch (err) {
        if (err instanceof Error) {
          let errorBody = '';
          const errorMessage = err.message;

          if (errorMessage.includes('NotFoundException')) {
            errorBody = formatMessage(messages['snackbar-error.entry-link-not-found'], {
              fieldName: field.name,
            });
          } else if (errorMessage.includes('PermissionException')) {
            errorBody = formatMessage(
              messages['snackbar-error.entry-link-restricted-permission'],
              {
                fieldName: field.name,
              }
            );
          } else {
            errorBody = formatMessage(messages['snackbar-error.entry-link-load-error'], {
              fieldName: field.name,
            });

            const error = new Error(
              `Could not load database with content id ${databaseContentId}: ${err}`
            );
            logError(error);
          }

          currentSnackbarState.setMessage(errorBody, {
            id: 'database-entry-link-value-edit',
            type: 'error',
            showCloseButton: true,
          });
        }

        return;
      }
    }, [databaseContentId, field.name, formatMessage, dispatch])
  );

  const resolved = useMemo(() => {
    let result: Value[];
    if (!targetDatabaseDraft) {
      result = [];
    } else {
      result = value.flatMap((entryRef) => {
        if (isNonEmptyEntryLinkValue(entryRef)) {
          const entry = getDatabaseEntryOptional(targetDatabaseDraft, entryRef.entryId);
          return entry ? [{ entryRef, entry }] : [{ entryRef }];
        } else {
          return [{ entryRef }];
        }
      });
    }

    return result;
  }, [targetDatabaseDraft, value]);

  const [inputValue, setInputValue] = useState<Value[]>(resolved);

  const store = defaultStoreUtils.useStore();

  const handleSingleChange = useCallback(
    (val: string | undefined) => {
      if (!val || !targetDatabaseDraft) {
        if (resolved.length > 0) {
          setInputValue([]);
          onChange([], {
            type: DatabaseFieldType.ENTRY_LINK,
            delta: {
              type: MultiValueChangeType.REMOVE,
              index: 0,
            },
          });
        }
      } else {
        const entry = targetDatabaseDraft.entriesById[val];
        const entryName = getEntryName(
          { database: targetDatabaseDraft, entry },
          store.getState()
        );
        const entryLinkValue = {
          _id: value[0]?._id ?? generateId(),
          entryId: val,
          title: entryName
            ? entryName
            : formatMessage(messages['unnamed-entities.unnamed-entry']),
        };

        setInputValue([{ ...entryLinkValue, entryRef: entryLinkValue }]);

        onChange([entryLinkValue], {
          type: DatabaseFieldType.ENTRY_LINK,
          delta:
            resolved.length > 0
              ? {
                  type: MultiValueChangeType.EDIT,
                  index: 0,
                  value: entryLinkValue,
                }
              : {
                  type: MultiValueChangeType.APPEND,
                  value: entryLinkValue,
                },
        });
      }
    },
    [targetDatabaseDraft, resolved.length, onChange, store, value, formatMessage]
  );

  const onItemChange = useCallback(
    (val: Value, newValueIndex: number, _: null, allValues: Value[]) => {
      setInputValue(allValues);

      onChange(
        allValues.map((v) => v.entryRef),
        {
          type: DatabaseFieldType.ENTRY_LINK,
          delta: {
            type: MultiValueChangeType.EDIT,
            value: val.entryRef,
            index: newValueIndex,
          },
        }
      );
    },
    [onChange]
  );

  const onItemAdd = useCallback(
    (val: Value, allValues: Value[]) => {
      setInputValue(allValues);

      onChange(
        allValues.map((v) => v.entryRef),
        {
          type: DatabaseFieldType.ENTRY_LINK,
          delta: {
            type: MultiValueChangeType.APPEND,
            value: val.entryRef,
          },
        }
      );
    },
    [onChange]
  );

  const onItemDelete = useCallback(
    (_: Value, removedIndex: number, allValues: Value[]) => {
      setInputValue(allValues);

      onChange(
        allValues.map((v) => v.entryRef),
        {
          type: DatabaseFieldType.ENTRY_LINK,
          delta: {
            type: MultiValueChangeType.REMOVE,
            index: removedIndex,
          },
        }
      );
    },
    [onChange]
  );

  const onItemReorder = useCallback(
    (_: Value, currentIndex: number, newIndex: number, allValues: Value[]) => {
      setInputValue(allValues);

      onChange(
        allValues.map((v) => v.entryRef),
        {
          type: DatabaseFieldType.ENTRY_LINK,
          delta: {
            type: MultiValueChangeType.REORDER,
            currentIndex,
            newIndex,
          },
        }
      );
    },
    [onChange]
  );

  const renderField: EditableListProps<Value, null>['renderField'] = useCallback(
    ({ value, autoFocus, onChange }) => (
      <EntryPicker
        database={targetDatabaseDraft}
        isLoading={isPending}
        isDisabled={isPending}
        noOptionsMessage={
          !databaseSpace || !databaseId
            ? formatMessage(messages['entry-link.no-options'])
            : ''
        }
        value={'entryId' in value.entryRef ? value.entryRef.entryId : ''}
        onChange={(val) => {
          if (val && targetDatabaseDraft) {
            const entry = targetDatabaseDraft.entriesById[val];
            const entryName = getEntryName(
              { database: targetDatabaseDraft, entry },
              store.getState()
            );
            onChange(
              {
                entryRef: {
                  _id: value.entryRef._id,
                  entryId: val,
                  title: entryName
                    ? entryName
                    : formatMessage(messages['unnamed-entities.unnamed-entry']),
                },
                entry,
              },
              null
            );
          } else {
            onChange(
              {
                entryRef: { _id: value.entryRef._id },
              },
              null
            );
          }
        }}
        autoFocus={autoFocus}
        isSeamless
        isLinkPreview
      />
    ),
    [targetDatabaseDraft, isPending, databaseSpace, databaseId, formatMessage, store]
  );

  if (multi || resolved.length > 1) {
    return (
      <EditableListWithPlaceholder<Value, null>
        getId={getId}
        values={inputValue}
        onItemChange={onItemChange}
        onAddNewItem={onItemAdd}
        onRemoveItem={onItemDelete}
        onReorder={onItemReorder}
        getNewValue={getNewValue}
        renderField={renderField}
        autoFocus={value.length === 0 ? autoFocus : undefined}
        addLabel="Add entry"
        noAdd={!multi}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    );
  } else {
    return (
      <EntryPicker
        database={targetDatabaseDraft}
        isLoading={isPending}
        isDisabled={isPending}
        noOptionsMessage={
          !databaseSpace || !databaseId
            ? formatMessage(messages['entry-link.no-options'])
            : ''
        }
        value={
          inputValue.length && 'entryId' in inputValue[0].entryRef
            ? inputValue[0].entryRef.entryId
            : undefined
        }
        onChange={handleSingleChange}
        onFocus={onFocus}
        onBlur={onBlur}
        autoFocus={autoFocus}
        isClearable
        isLinkPreview
      />
    );
  }
};

export default EntryLinkValueEdit;
